/* =========================================================================
   The Main Solution - theme behaviour
   Plain JavaScript, no libraries. Everything degrades safely without it.
   ========================================================================= */
(function () {
  'use strict';

  document.documentElement.classList.add('js');

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------- scroll reveal + donut draw ---------------- */
  function initReveal(root) {
    var els = (root || document).querySelectorAll('.rv');
    var donuts = (root || document).querySelectorAll('.donut');

    donuts.forEach(function (d) {
      var C = 2 * Math.PI * 52;
      var pct = parseFloat(d.dataset.pct) || 0;
      d.style.setProperty('--circ', C.toFixed(1));
      d.style.setProperty('--off', (C * (1 - pct / 100)).toFixed(1));
    });

    if (!('IntersectionObserver' in window) || reduce) {
      els.forEach(function (e) { e.classList.add('in'); });
      donuts.forEach(function (d) { d.classList.add('go'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        en.target.classList.add('in');
        var inner = en.target.querySelectorAll ? en.target.querySelectorAll('.donut') : [];
        inner.forEach(function (d) { d.classList.add('go'); });
        if (en.target.classList.contains('donut')) en.target.classList.add('go');
        io.unobserve(en.target);
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -6% 0px' });

    els.forEach(function (e) { io.observe(e); });

    /* Safety net. The observer does not fire in a hidden or prerendered tab, and
       .rv starts at opacity 0, so without this the page could stay blank. If
       nothing has revealed itself after a couple of seconds, show everything. */
    setTimeout(function () {
      if (document.querySelector('.rv.in')) return;
      els.forEach(function (e) { e.classList.add('in'); });
      donuts.forEach(function (d) { d.classList.add('go'); });
    }, 2200);
  }

  /* ---------------- problem / solution tabs ---------------- */
  function initTabs(root) {
    (root || document).querySelectorAll('[data-tabs]').forEach(function (wrap) {
      var tabs = Array.prototype.slice.call(wrap.querySelectorAll('[role="tab"]'));
      var panel = wrap.querySelector('[role="tabpanel"]');
      if (!tabs.length || !panel) return;

      var probEl = panel.querySelector('[data-slot="problem"]');
      var fixEl = panel.querySelector('[data-slot="fix"]');

      function show(btn) {
        tabs.forEach(function (b) { b.setAttribute('aria-selected', String(b === btn)); });
        if (probEl) probEl.textContent = btn.dataset.problem || '';
        if (fixEl) fixEl.textContent = btn.dataset.fix || '';
        panel.setAttribute('aria-labelledby', btn.id);
        if (!reduce && panel.animate) {
          panel.animate([{ opacity: 0.35 }, { opacity: 1 }], { duration: 260, easing: 'cubic-bezier(.16,1,.3,1)' });
        }
      }

      tabs.forEach(function (b, i) {
        b.addEventListener('click', function () { show(b); });
        b.addEventListener('keydown', function (e) {
          var dir = (e.key === 'ArrowDown' || e.key === 'ArrowRight') ? 1
                  : (e.key === 'ArrowUp' || e.key === 'ArrowLeft') ? -1 : 0;
          if (!dir) return;
          e.preventDefault();
          var next = tabs[(i + dir + tabs.length) % tabs.length];
          next.focus();
          show(next);
        });
      });
    });
  }

  /* ---------------- product gallery ---------------- */
  function initGallery(root) {
    (root || document).querySelectorAll('[data-gallery-root]').forEach(function (gal) {
      var main = gal.querySelector('[data-gallery-main]');
      var thumbs = gal.querySelectorAll('.gal__thumbs button');
      if (!main || !thumbs.length) return;
      var list = [].slice.call(thumbs);

      function go(i) {
        var n = (i + list.length) % list.length;
        list.forEach(function (b) { b.setAttribute('aria-current', 'false'); });
        list[n].setAttribute('aria-current', 'true');
        main.src = list[n].dataset.full;
      }
      function current() {
        var i = list.findIndex(function (b) { return b.getAttribute('aria-current') === 'true'; });
        return i < 0 ? 0 : i;
      }

      list.forEach(function (btn, i) {
        btn.addEventListener('click', function () { go(i); });
      });

      var prev = gal.querySelector('[data-gal-prev]');
      var next = gal.querySelector('[data-gal-next]');
      if (prev) prev.addEventListener('click', function () { go(current() - 1); });
      if (next) next.addEventListener('click', function () { go(current() + 1); });

      // swipe on touch
      var sx = null;
      var stage = gal.querySelector('.gal__main');
      if (stage) {
        stage.addEventListener('touchstart', function (e) { sx = e.changedTouches[0].clientX; }, { passive: true });
        stage.addEventListener('touchend', function (e) {
          if (sx === null) return;
          var dx = e.changedTouches[0].clientX - sx;
          if (Math.abs(dx) > 45) go(current() + (dx < 0 ? 1 : -1));
          sx = null;
        }, { passive: true });
      }
    });
  }

  /* ---------------- buy box: packs, quantity, sticky bar ---------------- */
  function initBuyBox(root) {
    var scope = root || document;
    var qtyInput = scope.querySelector('[data-qty-input]');
    var totalEl  = scope.querySelector('[data-price-total]');
    var unitEl   = scope.querySelector('[data-price-unit]');
    var packs    = scope.querySelectorAll('[data-packs] .pack');
    var stickyP  = scope.querySelector('[data-sticky-price]');

    function paint(total, unit, qty) {
      if (totalEl && total) totalEl.textContent = total;
      if (stickyP && total) stickyP.textContent = total;
      if (unitEl) {
        if (unit && qty > 1) { unitEl.textContent = unit + ' each'; unitEl.hidden = false; }
        else { unitEl.hidden = true; }
      }
    }

    packs.forEach(function (pack) {
      pack.addEventListener('click', function () {
        packs.forEach(function (p) { p.setAttribute('aria-checked', 'false'); });
        pack.setAttribute('aria-checked', 'true');
        var q = parseInt(pack.dataset.qty, 10) || 1;
        if (qtyInput) qtyInput.value = q;
        paint(pack.dataset.total, pack.dataset.unit, q);
      });
    });

    // if a pack is preselected, make the quantity agree on load
    var checked = scope.querySelector('[data-packs] .pack[aria-checked="true"]');
    if (checked && qtyInput) {
      qtyInput.value = parseInt(checked.dataset.qty, 10) || 1;
      paint(checked.dataset.total, checked.dataset.unit, parseInt(checked.dataset.qty, 10) || 1);
    }

    scope.querySelectorAll('[data-qty-step]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!qtyInput) return;
        var next = (parseInt(qtyInput.value, 10) || 1) + parseInt(btn.dataset.qtyStep, 10);
        qtyInput.value = Math.max(1, next);
        // typing a quantity by hand means no pack is selected any more
        packs.forEach(function (p) { p.setAttribute('aria-checked', 'false'); });
      });
    });

    /* Kaching and Seal inject themselves into the buy box after load, and not as
       section blocks, so Liquid cannot see them. Watch for their markup instead
       and stand the theme's own controls down the moment one appears.

       THE ELEMENT EXISTING IS NOT THE SAME AS THE APP HAVING AN OFFER, and that
       distinction is the whole point of the `drew` check below. Kaching injects
       an empty <kaching-bundle> on EVERY product, whether or not a deal is
       configured for that one. Testing for the tag alone therefore hid the
       theme's own price on every product Kaching has no deal for, and those
       pages ended up showing no offer at all: not Kaching's, because there is
       none, and not ours, because we stood down for an empty placeholder. That
       was live on the Vitamin Complex and all five bundles. */
    var box = scope.querySelector('[data-buybox]') || document.querySelector('[data-buybox]');
    if (box && box.dataset.nativePacks === 'auto') {
      var APP = 'kaching-bundle, kaching-bundles-block, .kaching-bundles__block--loaded,' +
                ' .sealsubs-container, .sealsubs-target-element';
      var drew = function (el) {
        return !!el && el.children.length > 0 && el.getBoundingClientRect().height > 8;
      };
      var appHere = function () {
        var nodes = box.querySelectorAll(APP);
        for (var i = 0; i < nodes.length; i++) { if (drew(nodes[i])) return true; }
        return drew(document.querySelector('.kaching-bundles__block--loaded'));
      };
      var useApp = function () {
        box.classList.remove('native-ok'); box.classList.add('has-app-offer');
      };
      var useOurs = function () { box.classList.add('native-ok'); };

      if (appHere()) {
        useApp();
      } else if ('MutationObserver' in window) {
        /* Two stages, because the failure modes pull opposite ways. Reveal ours
           early so a page is never priceless for long, but keep watching well
           past that, because an app rendering late would otherwise leave two
           offers stacked. The classes are swapped, never just added to. */
        var mo = new MutationObserver(function () { if (appHere()) { useApp(); mo.disconnect(); } });
        mo.observe(document.body, { childList: true, subtree: true });
        setTimeout(function () { if (!appHere()) useOurs(); }, 1400);
        setTimeout(function () { mo.disconnect(); if (!appHere()) useOurs(); }, 4000);
      } else {
        useOurs();
      }
    }

    // sticky bar appears once the real button has scrolled away
    var bar = scope.querySelector('[data-sticky]');
    var addBtn = scope.querySelector('[data-add]');
    if (bar && addBtn && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          bar.classList.toggle('on', !en.isIntersecting && en.boundingClientRect.top < 0);
          bar.setAttribute('aria-hidden', String(en.isIntersecting));
        });
      }, { threshold: 0 }).observe(addBtn);

      var stickyAdd = bar.querySelector('[data-sticky-add]');
      if (stickyAdd) stickyAdd.addEventListener('click', function () { addBtn.click(); });
    }
  }

  /* ---------------- before / after slider ---------------- */
  function initBeforeAfter(root) {
    (root || document).querySelectorAll('[data-ba]').forEach(function (ba) {
      var input = ba.querySelector('input[type="range"]');
      if (!input) return;
      var apply = function () { ba.style.setProperty('--pos', input.value + '%'); };
      apply();
      input.addEventListener('input', apply);
      // dragging the bar itself, not just the invisible thumb
      var drag = function (e) {
        if (e.buttons !== 1 && e.type === 'pointermove') return;
        var r = ba.getBoundingClientRect();
        var pct = Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100));
        input.value = Math.round(pct);
        apply();
      };
      ba.addEventListener('pointerdown', drag);
      ba.addEventListener('pointermove', drag);
    });
  }

  /* ---------------- timeline ---------------- */
  function initTimeline(root) {
    (root || document).querySelectorAll('[data-timeline]').forEach(function (tl) {
      var steps = [].slice.call(tl.querySelectorAll('[role="tab"]'));
      var panel = tl.querySelector('[role="tabpanel"]');
      if (!steps.length || !panel) return;
      var lab = panel.querySelector('[data-slot="label"]');
      var body = panel.querySelector('[data-slot="body"]');

      function show(btn) {
        steps.forEach(function (b) { b.setAttribute('aria-selected', String(b === btn)); });
        if (lab) lab.textContent = btn.dataset.label || '';
        if (body) body.textContent = btn.dataset.body || '';
        panel.setAttribute('aria-labelledby', btn.id);
        if (!reduce && panel.animate) {
          panel.animate([{ opacity: 0.4 }, { opacity: 1 }], { duration: 240, easing: 'cubic-bezier(.16,1,.3,1)' });
        }
      }
      steps.forEach(function (b, i) {
        b.addEventListener('click', function () { show(b); });
        b.addEventListener('keydown', function (e) {
          var d = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1
                : (e.key === 'ArrowLeft' || e.key === 'ArrowUp') ? -1 : 0;
          if (!d) return;
          e.preventDefault();
          var n = steps[(i + d + steps.length) % steps.length];
          n.focus(); show(n);
        });
      });
    });
  }

  /* ---------------- header: menu, dropdowns, search ---------------- */
  function initMenu(root) {
    var scope = root || document;

    scope.querySelectorAll('[data-menu-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var hdr = btn.closest('.hdr');
        var open = hdr.dataset.open === 'true';
        hdr.dataset.open = String(!open);
        btn.setAttribute('aria-expanded', String(!open));
        // opening the menu must not leave the bar slid off the top of the screen
        if (!open) {
          var shell = hdr.closest('.hdr-shell');
          if (shell) shell.dataset.hide = 'false';
          hdr.dataset.hide = 'false';
        } else {
          /* closing collapses the dropdowns, so the next open starts clean and a
             stale aria-expanded cannot leak onto the desktop hover nav if the
             window is widened mid-session */
          hdr.querySelectorAll('[data-drop][aria-expanded="true"]').forEach(function (b) {
            b.setAttribute('aria-expanded', 'false');
          });
        }
      });
    });

    // dropdowns: hover handles desktop in CSS, this covers tap and keyboard
    var drops = [].slice.call(scope.querySelectorAll('[data-drop]'));
    drops.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var open = btn.getAttribute('aria-expanded') === 'true';
        drops.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
        btn.setAttribute('aria-expanded', String(!open));
      });
    });
    if (drops.length) {
      document.addEventListener('click', function () {
        drops.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') drops.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      });
    }

    scope.querySelectorAll('[data-search-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var panel = btn.closest('.hdr').querySelector('[data-search-panel]');
        if (!panel) return;
        var on = panel.classList.toggle('on');
        btn.setAttribute('aria-expanded', String(on));
        if (on) { var i = panel.querySelector('input'); if (i) i.focus(); }
      });
    });
  }

  /* ---------------- header that hides on the way down ----------------

     A copy of what the live store already does, which is Dawn's on-scroll-up
     sticky header. Four details matter and all four are deliberate:

       - for the whole first screen it is not stuck at all. It scrolls away with
         the page, and only starts sticking once you have scrolled past it;
       - it then moves on ANY scroll, with no pixel deadzone, so one notch of the
         wheel upwards brings it straight back;
       - nothing happens between the top of the page and the header's own bottom
         edge, so the state is held rather than reset mid-scroll;
       - the transition is only switched on once you have scrolled up at least
         once, which is why the first hide is instant and why returning to the
         very top drops it into place without sliding.
  */
  function initStickyHeader() {
    var hdr = document.querySelector('.hdr[data-autohide="true"]');
    if (!hdr || initStickyHeader.done) return;
    initStickyHeader.done = true;

    /* The sticky element is the section wrapper, not the bar. See the long note in
       header.liquid: sticking the bar itself gave it nowhere to travel, so it never
       stuck at all. Everything below drives the wrapper. */
    var shell = hdr.closest('.hdr-shell') || hdr.parentElement || hdr;
    initStickyHeader.shell = shell;

    var top = 0, height = hdr.offsetHeight, last = window.scrollY || 0, queued = false;

    /* Only measurable while it is sitting in its natural place. Once it is stuck
       the rect reads the viewport, not the document, so keep the last good one. */
    function measure() {
      height = shell.offsetHeight || hdr.offsetHeight;
      shell.style.setProperty('--hdr-h', height + 'px');
      if ((window.scrollY || 0) === 0) top = shell.getBoundingClientRect().top;
    }

    function shut() {
      hdr.querySelectorAll('[data-drop][aria-expanded="true"]')
         .forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      var panel = hdr.querySelector('[data-search-panel].on');
      if (panel) {
        panel.classList.remove('on');
        hdr.querySelectorAll('[data-search-toggle]')
           .forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      }
    }

    function hide()   { shell.dataset.stick = 'true'; shell.dataset.hide = 'true'; shut(); }
    function reveal() { shell.dataset.stick = 'true'; shell.dataset.animate = 'true'; shell.dataset.hide = 'false'; }
    function reset()  { shell.dataset.stick = 'false'; shell.dataset.animate = 'false'; shell.dataset.hide = 'false'; }

    function apply() {
      queued = false;
      var y = window.scrollY || 0;
      var bottom = top + height;

      if (y > last && y > bottom) {
        hide();
      } else if (y < last && y > bottom) {
        reveal();
      } else if (y <= top) {
        reset();
        measure();
      }
      last = y;
    }

    measure();
    window.addEventListener('scroll', function () {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(apply);
    }, { passive: true });
    window.addEventListener('resize', measure, { passive: true });

    // coming back from another page mid-scroll should not start hidden
    window.addEventListener('pageshow', function () {
      shell.dataset.hide = 'false';
      last = window.scrollY || 0;
    });
  }

  /* Two-state toggles, same trick as the scalp map: the radios hold the choice so
     it still works without scripts, and the value is mirrored onto data-on so the
     rules key off an ancestor attribute, which always repaints. */
  function initSwap(root) {
    var boxes = (root || document).querySelectorAll('[data-swap]');
    Array.prototype.forEach.call(boxes, function (box) {
      if (box.dataset.swapReady === 'true') return;
      box.dataset.swapReady = 'true';
      var inputs = box.querySelectorAll('input[type="radio"]');
      if (!inputs.length) return;
      function sync() {
        for (var i = 0; i < inputs.length; i++) {
          if (inputs[i].checked) { box.dataset.on = inputs[i].value; return; }
        }
      }
      Array.prototype.forEach.call(inputs, function (input) {
        input.addEventListener('change', sync);
      });
      sync();
    });
  }

  /* Depth cross-section on the Elixir page.

     Drag anywhere on the figure, or use the slider, or click a tick. All three
     land in the same place: --d drives the wash and the scan line, data-layer
     picks the panel, data-deep is the payoff at the bottom. The slider is a real
     range input so it works with a keyboard and reads correctly to a screen
     reader; the drag is just a nicer way to reach the same value. */
  function initDepth(root) {
    var boxes = (root || document).querySelectorAll('[data-depth]');
    Array.prototype.forEach.call(boxes, function (box) {
      if (box.dataset.depthReady === 'true') return;
      box.dataset.depthReady = 'true';

      var input = box.querySelector('input[type="range"]');
      var fig = box.querySelector('[data-depth-fig]');
      if (!input) return;
      var ticks = box.querySelectorAll('[data-at]');

      function sync() {
        var v = parseFloat(input.value) || 0;
        box.style.setProperty('--d', v);

        var layer = 1, deepest = 0;
        Array.prototype.forEach.call(ticks, function (t) {
          var at = parseFloat(t.dataset.at) || 0;
          var reached = v >= at;
          t.dataset.reached = String(reached);
          if (reached && at >= deepest) {
            deepest = at;
            layer = parseInt(t.dataset.layer, 10) || 1;
          }
        });
        box.dataset.layer = String(layer);
        box.dataset.deep = String(v >= 84);
      }

      input.addEventListener('input', sync);

      if (fig) {
        var grab = function (e) {
          if (e.type === 'pointermove' && e.buttons !== 1) return;
          var r = fig.getBoundingClientRect();
          var pct = Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100));
          input.value = Math.round(pct);
          box.dataset.touched = 'true';
          sync();
        };
        fig.addEventListener('pointerdown', grab);
        fig.addEventListener('pointermove', grab);
      }

      Array.prototype.forEach.call(box.querySelectorAll('[data-jump]'), function (b) {
        b.addEventListener('click', function () {
          input.value = b.dataset.jump;
          box.dataset.touched = 'true';
          sync();
        });
      });

      input.addEventListener('input', function () { box.dataset.touched = 'true'; });
      sync();
    });
  }

  /* Scalp map.

     The radios drive the panel fine on their own, but the sibling selectors do
     not reliably repaint the diagram's subtree when the checked one changes,
     which left the artwork lit on whichever zone loaded first. Mirroring the
     choice onto data-zone on the container fixes that: the highlight rules key
     off an ancestor attribute, which always invalidates. */
  function initScalpMap(root) {
    var maps = (root || document).querySelectorAll('[data-scalp]');
    Array.prototype.forEach.call(maps, function (map) {
      if (map.dataset.scalpReady === 'true') return;
      map.dataset.scalpReady = 'true';
      var inputs = map.querySelectorAll('input[type="radio"]');
      if (!inputs.length) return;
      function sync() {
        var pick = 1;
        for (var i = 0; i < inputs.length; i++) {
          if (inputs[i].checked) { pick = i + 1; break; }
        }
        map.dataset.zone = String(pick);
      }
      Array.prototype.forEach.call(inputs, function (input) {
        input.addEventListener('change', sync);
      });
      sync();
    });
  }

  function initAll(root) {
    initReveal(root);
    initStickyHeader();
    initTabs(root);
    initGallery(root);
    initBuyBox(root);
    initBeforeAfter(root);
    initTimeline(root);
    initMenu(root);
    initScalpMap(root);
    initSwap(root);
    initDepth(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  /* Re-run when a section is edited in the Shopify theme editor,
     otherwise tabs and reveals stop working while the merchant is editing. */
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:select', function (e) { initReveal(e.target); });
})();
